import * as _ from 'lodash';
import { useSelector } from 'react-redux';

import { plural } from '../../../../shared/helpers';
import { IgnoredKind } from '../../../../shared/issuesV2';
import { isFreeTier } from '../../../../shared/subscriptions';
import { window } from '../../../globals';
import { useMultipleModals } from '../../../hooks';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { ignoreOrResolveWord } from '../../../utils';
import { Icon, icons } from '../../generic';
import DropdownButton from '../../generic/DropdownButton';
import ReadOnlySection from '../../generic/ReadOnlySection';
import {
  ExportIssuesModalInboxContainer,
  IgnoreIssuesModalInboxContainer,
  UnignoreIssuesModalInboxContainer,
  UnlinkIssuesModalInboxContainer,
} from '../modals';

import useGenerateCSVIssues from './hooks/useGenerateCSVIssues';
import useUserHasIgnorePermission from './hooks/useUserHasIgnorePermission';

const IssuesActionDropdown = () => {
  const { ignoreModal, unignoreModal, unlinkModal, exportModal } = useMultipleModals('ignore', 'unignore', 'unlink', 'export');

  const isLoading = useSelector(IssuesV2Store.selectors.getIsLoadingIssues);
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const issueStatus = useSelector(IssuesV2Store.selectors.getStatus);
  const jiraTicketCount = useSelector(IssuesV2Store.selectors.getJiraTicketCountForSelectedIssues);

  const selectedIssuesCount = useSelector(IssuesV2Store.selectors.getSelectedIssuesCount);
  const selectedIssues = useSelector(IssuesV2Store.selectors.getSelectedIssues);
  const selectedRevisions = useSelector(IssuesV2Store.selectors.getSelectedRevisions);
  const hasSelection = !_.isEmpty(selectedIssues) || !_.isEmpty(selectedRevisions);
  const generateRequestUrl = useSelector(IssuesV2Store.selectors.getGenerateSelectedIssuesUrl);
  const { isLoading: isGenerateCSVIssuesLoading, mutate: generateCSVIssues } = useGenerateCSVIssues();

  const isComparisonModeOn = useSelector(IssuesV2Store.selectors.getComparisonMode);

  const { hasIgnorePermission } = useUserHasIgnorePermission();

  if (isComparisonModeOn) {
    return null;
  }

  // For now, the buttons won't do anything and that's why onClick={_.noop}
  // In a following PR, we'll implement the modals that these buttons will open
  return (
    <>
      {ignoreModal.isOpen && (
        <IgnoreIssuesModalInboxContainer
          show={ignoreModal.isOpen}
          onHide={ignoreModal.onClose}
          selectedIssuesCount={selectedIssuesCount}
        />
      )}
      {unignoreModal.isOpen && (
        <UnignoreIssuesModalInboxContainer
          show={unignoreModal.isOpen}
          onHide={unignoreModal.onClose}
          selectedIssuesCount={selectedIssuesCount}
          areAnyIgnoredByRule={
            selectedIssues === 'all'
              || selectedIssues.some(issue =>
                issue.projects.some(project => project.ignoredKind?.ignoreType === IgnoredKind.IssueException))
          }
        />
      )}
      {unlinkModal.isOpen && <UnlinkIssuesModalInboxContainer show={unlinkModal.isOpen} onHide={unlinkModal.onClose} />}
      {exportModal.isOpen && (
        <ExportIssuesModalInboxContainer
          show={exportModal.isOpen}
          issueCategory={issueCategory}
          selectedIssuesCount={selectedIssuesCount}
          onHide={exportModal.onClose}
        />
      )}
      <DropdownButton
        id="IssuesGroupingDropdown"
        title="Actions"
        size="medium"
        disabled={selectedIssuesCount === 0}
      >
        <DropdownButton.Item
          disabled={!hasSelection || isLoading || !hasIgnorePermission}
          onSelect={issueStatus === 'active' ? ignoreModal.onOpen : unignoreModal.onOpen}
        >
          {issueStatus === 'active' ? 'Ignore' : `Un${ignoreOrResolveWord().toLowerCase()}`}
        </DropdownButton.Item>
        <DropdownButton.Item
          disabled={!hasSelection || isLoading}
          onSelect={() => {
            if (isFreeTier({
              plan: window.user?.organization?.stripe_plan,
              stripePriceLimit: window.user?.organization?.stripePriceLimit,
            })) {
              window.showPlanSelectorModal();
              return;
            }

            exportModal.onOpen();
          }}
        >
          <ReadOnlySection
            isReadOnly={
              isFreeTier({
                plan: window.user?.organization?.stripe_plan,
                stripePriceLimit: window.user?.organization?.stripePriceLimit,
              })
            }
            isClickable>
            Create ticket
          </ReadOnlySection>
          {isFreeTier({
            plan: window.user?.organization?.stripe_plan,
            stripePriceLimit: window.user?.organization?.stripePriceLimit,
          }) && (<Icon icon={icons.lockOutline} />)}
        </DropdownButton.Item>
        <DropdownButton.Item
          disabled={jiraTicketCount === 0 || isLoading}
          onSelect={unlinkModal.onOpen}
        >
          Unlink {plural(jiraTicketCount, 'ticket')}
        </DropdownButton.Item>
        <DropdownButton.Item
          onClick={() => generateCSVIssues(generateRequestUrl)}
          disabled={!hasSelection || isLoading || isGenerateCSVIssuesLoading}
        >
          Generate CSV
        </DropdownButton.Item>
      </DropdownButton>
    </>
  );
};

export default IssuesActionDropdown;
